import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Space, Table, Tag, Popconfirm } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { quittanceStatuses } from '../../utils/constants/tagColors';
import useErrorMessage from '../../utils/ErrorMessage';
import useAuthContext from '../../contexts/AuthContext';
import useContractsContext from './ContractsContext';

const { Column } = Table;

const PaperWorkFeeTable = ({ data: paperwork_fee_invoice }) => {
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI, user } = useAuthContext();
  const [currentYear, setCurrentYear] = useState();
  const [uniqueYears, setUniqueYears] = useState([]);
  const isCustomer = user?.role === 'guests:GUEST';
  const { forceRefresh, setForceRefresh } = useContractsContext();

  const patchPaperworkFee = async (status, id) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/paperWorkFee/${id}`,
        body:
          status === 'NOT_PAID' ? { status: 'PAID' } : { status: 'NOT_PAID' }
      });
      setForceRefresh(!forceRefresh);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    if (paperwork_fee_invoice.length) {
      const years = paperwork_fee_invoice.map((q) =>
        moment(q.period_date).year()
      );
      setUniqueYears([...new Set(years)]);
    }
  }, [paperwork_fee_invoice]);

  useEffect(() => {
    if (uniqueYears.length) setCurrentYear(uniqueYears[uniqueYears.length - 1]);
  }, [uniqueYears]);

  return (
    <>
      <Space style={{ margin: '4px 0 16px 0' }}>
        {uniqueYears
          .sort((a, b) => a - b)
          .map((year) => (
            <Button
              key={year}
              type={year === currentYear ? 'primary' : 'link'}
              onClick={() => setCurrentYear(year)}
            >
              {year}
            </Button>
          ))}
      </Space>
      <Table
        dataSource={paperwork_fee_invoice
          .filter(
            (q) =>
              moment(q.period_date).year() === currentYear &&
              q.paperworkFee_status !== 'PENDING'
          )
          .map(({ _id: key, ...d }) => ({ ...d, key }))}
        pagination={{ pageSize: 12, hideOnSinglePage: true }}
        scroll={{ x: 800 }}
      >
        <Column
          title={t('paperWorkFee.form.period_date')}
          dataIndex="period_date"
          render={(date) => date && moment(date).format('MM/YYYY')}
        />
        <Column
          title={t('paperWorkFee.form.paperworkFee_status')}
          dataIndex="paperworkFee_status"
          render={(paperworkFee_status) => (
            <Tag color={quittanceStatuses[paperworkFee_status]}>
              {t(`paperWorkFee.status.${paperworkFee_status}`)}
            </Tag>
          )}
        />
        <Column
          title={t('paperWorkFee.form.paperwork_fee_payment_choices')}
          dataIndex="paperwork_fee_payment_choices"
          render={(paperwork_fee_payment_choices) => (
            <Tag color={quittanceStatuses[paperwork_fee_payment_choices]}>
              {t(`paperWorkFee.status.${paperwork_fee_payment_choices}`)}
            </Tag>
          )}
        />
        <Column
          title={t('paperWorkFee.form.amount')}
          dataIndex="amount"
          render={(p) => `${numeral(p || 0).format('0,0.00')}€`}
        />
        <Column
          title={t('paperWorkFee.form.paid_date')}
          dataIndex="paid_date"
          render={(paid_date) =>
            paid_date && moment(paid_date).format('DD/MM/YYYY')
          }
        />
        {paperwork_fee_invoice[0]?.paperwork_fee_payment_choices !== 'FREE' && (
          <>
            <Column
              title={t('paperWorkFee.form.payback')}
              dataIndex="paperworkFee_status"
              render={(paperworkFee_status, { amount }) =>
                paperworkFee_status === 'PAID' ? (
                  <Tag color="green">
                    {`${numeral(amount || 0).format('0,0.00')}€`}
                  </Tag>
                ) : (
                  'En attente de paiement'
                )
              }
            />
            <Column
              title={t('paperWorkFee.form.payback_date')}
              dataIndex="payback_date"
              render={(payback_date) =>
                payback_date ? moment(payback_date).format('DD/MM/YYYY') : ''
              }
            />
          </>
        )}
        {!isCustomer &&
          paperwork_fee_invoice[0]?.paperwork_fee_payment_choices ===
            'FREE' && (
            <Column
              fixed="right"
              title={t('paperWorkFee.form.action')}
              dataIndex="paperworkFee_status"
              render={(paperworkFee_status, { key }) => {
                return (
                  <Popconfirm
                    title={t(
                      `paperWorkFee.popconfirm.title${
                        paperworkFee_status === 'NOT_PAID'
                          ? '_accept'
                          : 'remove'
                      }`
                    )}
                    okText={t('paperWorkFee.popconfirm.ok')}
                    cancelText={t('paperWorkFee.popconfirm.cancel')}
                    okButtonProps={{ type: 'danger' }}
                    onConfirm={() => {
                      patchPaperworkFee(paperworkFee_status, key);
                    }}
                    icon={<WarningOutlined />}
                  >
                    <Button
                      type={
                        paperworkFee_status === 'NOT_PAID'
                          ? 'danger'
                          : 'primary'
                      }
                    >
                      {t(`paperWorkFee.status.${paperworkFee_status}`)}
                    </Button>
                  </Popconfirm>
                );
              }}
            />
          )}
      </Table>
    </>
  );
};

PaperWorkFeeTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({}))
};

PaperWorkFeeTable.defaultProps = {
  data: []
};

export default PaperWorkFeeTable;
